import { useEffect } from 'react';
import {
  Browser,
  TileLayer,
  type LatLng,
  type LatLngBoundsExpression,
  type TileLayerOptions,
} from 'leaflet';
import { useMap } from 'react-leaflet';
import { MAP_LARGE_LABEL_ZOOM_SHIFT, MAP_MAX_TILE_ZOOM } from './map.constants';

const STANDARD_SCREEN_TILE_ZOOM_BIAS = 0.25;
const TILE_ZOOM_BIAS = Browser.retina ? 0 : STANDARD_SCREEN_TILE_ZOOM_BIAS;
const RETINA_TILE_ZOOM_SHIFT = Browser.retina ? 1 : 0;
const STANDARD_TILE_SIZE_PX = 256;
const LARGE_LABEL_TILE_SIZE_PX = STANDARD_TILE_SIZE_PX * 2 ** MAP_LARGE_LABEL_ZOOM_SHIFT;

interface GridLayerInternals {
  _setView: (center: LatLng, zoom: number, noPrune?: boolean, noUpdate?: boolean) => void;
  _setZoomTransforms: (center: LatLng, zoom: number) => void;
}

const gridLayerInternals = TileLayer.prototype as unknown as GridLayerInternals;

class LessStretchedTileLayer extends TileLayer {
  _setView(center: LatLng, zoom: number, noPrune?: boolean, noUpdate?: boolean) {
    gridLayerInternals._setView.call(this, center, zoom + TILE_ZOOM_BIAS, noPrune, noUpdate);
    gridLayerInternals._setZoomTransforms.call(this, center, zoom);
  }
}

function buildTileOptions(
  bounds: LatLngBoundsExpression,
  maxZoom: number,
  hasLargeLabels: boolean,
): TileLayerOptions {
  const labelZoomShift = hasLargeLabels ? MAP_LARGE_LABEL_ZOOM_SHIFT : 0;
  const options: TileLayerOptions = {
    bounds,
    detectRetina: true,
    maxZoom,
    maxNativeZoom: MAP_MAX_TILE_ZOOM + labelZoomShift - RETINA_TILE_ZOOM_SHIFT,
  };
  if (!hasLargeLabels) return options;

  return {
    ...options,
    tileSize: LARGE_LABEL_TILE_SIZE_PX,
    zoomOffset: -MAP_LARGE_LABEL_ZOOM_SHIFT,
  };
}

interface SharpTileLayerProps {
  url: string;
  bounds: LatLngBoundsExpression;
  maxZoom: number;
  attribution?: string | undefined;
  hasLargeLabels?: boolean;
}

export function SharpTileLayer({
  url,
  bounds,
  maxZoom,
  attribution,
  hasLargeLabels = false,
}: SharpTileLayerProps) {
  const map = useMap();

  useEffect(() => {
    const options = buildTileOptions(bounds, maxZoom, hasLargeLabels);
    if (attribution) options.attribution = attribution;
    const layer = new LessStretchedTileLayer(url, options).addTo(map);
    return () => {
      layer.remove();
    };
  }, [map, url, bounds, maxZoom, attribution, hasLargeLabels]);

  return null;
}
